// frontend/src/components/ManagerView.js
import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { getAllPayouts, getQuotas } from '../api/client';

const fmt = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(n);

// The three months of a quarter, for the month filter: [{ key: '2026-07', label: 'July' }, …]
function quarterMonths(quarterStr) {
  const [year, q] = (quarterStr || '').split('-Q').map(Number);
  if (!year || !q) return [];
  return [0, 1, 2].map(i => {
    const mo = (q - 1) * 3 + i;
    return {
      key:   `${year}-${String(mo + 1).padStart(2, '0')}`,
      label: new Date(Date.UTC(year, mo, 1)).toLocaleString('en-US', { month: 'long', timeZone: 'UTC' }),
    };
  });
}

function getCurrentQuarter() {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}-Q${q}`;
}

export default function ManagerView() {
  const [quarter, setQuarter] = useState(getCurrentQuarter());
  // '' = whole quarter; otherwise a YYYY-MM inside it. Scopes the KPIs, leaderboard
  // and rep figures. The progress-to-target bar always uses the full quarter because
  // the quota is quarterly.
  const [month, setMonth] = useState('');
  const [selectedRep, setSelectedRep] = useState(null);

  const { data, loading } = useApi(() => getAllPayouts({ quarter }), [quarter]);
  const { data: quotaData } = useApi(() => getQuotas(quarter), [quarter]);

  const payouts = data?.payouts || [];
  const months  = quarterMonths(quarter);
  const monthLabel = months.find(m => m.key === month)?.label || null;
  const periodLabel = monthLabel || 'QTD';

  // Rows in scope for the KPIs / leaderboard (the selected month, or the quarter).
  const scoped = month ? payouts.filter(p => p.payout_month === month) : payouts;

  // Every month of the quarter per rep — the drill-down table shows the whole
  // quarter (so QTD cumulative reads correctly) and highlights the selected month.
  const allMonthsByRep = {};
  for (const p of payouts) (allMonthsByRep[p.rep_id] ||= []).push(p);

  // Aggregate by rep
  const repMap = {};
  for (const p of scoped) {
    if (!repMap[p.rep_id]) {
      repMap[p.rep_id] = {
        rep_id: p.rep_id, rep_name: p.rep_name,
        total: 0, hwSw: 0, services: 0, fod: 0,
        hwSwGP: 0, servicesGP: 0, msaGP: 0, renewalMsaGP: 0, fodGP: 0,
        months: [],
      };
    }
    const r = repMap[p.rep_id];
    r.total      += parseFloat(p.total_payout) || 0;
    r.hwSw       += parseFloat(p.hw_sw_payout)  || 0;
    r.services   += parseFloat(p.services_payout) || 0;
    r.fod        += parseFloat(p.fod_payout) || 0;
    r.hwSwGP       += parseFloat(p.hw_sw_gp_this_month) || 0;
    r.servicesGP   += parseFloat(p.services_gp_this_month) || 0;   // Professional Services
    r.msaGP        += parseFloat(p.msa_gp_this_month) || 0;
    r.renewalMsaGP += parseFloat(p.renewal_msa_gp_this_month) || 0;
    r.fodGP        += parseFloat(p.fod_gp_this_month) || 0;
    r.months.push(p);
  }

  const reps = Object.values(repMap).sort((a, b) => b.total - a.total);
  const teamTotal        = reps.reduce((s, r) => s + r.total, 0);       // cumulative commission
  const teamHwSwGP       = reps.reduce((s, r) => s + r.hwSwGP, 0);
  const teamServicesGP   = reps.reduce((s, r) => s + r.servicesGP, 0);  // Professional Services
  const teamMsaGP        = reps.reduce((s, r) => s + r.msaGP, 0);
  const teamRenewalMsaGP = reps.reduce((s, r) => s + r.renewalMsaGP, 0);
  const teamFodGP        = reps.reduce((s, r) => s + r.fodGP, 0);

  // Progress to target — total shipped GP across all categories, all reps, for the
  // WHOLE quarter (the quota is quarterly, so a month filter must not shrink it).
  const teamTotalGP = payouts.reduce((s, p) =>
    s + (parseFloat(p.hw_sw_gp_this_month) || 0) + (parseFloat(p.services_gp_this_month) || 0)
      + (parseFloat(p.msa_gp_this_month) || 0) + (parseFloat(p.renewal_msa_gp_this_month) || 0)
      + (parseFloat(p.fod_gp_this_month) || 0), 0);
  const TARGET_GP   = quotaData?.teamViewQuotaGp ?? 1_800_000; // finance-configurable

  const targetPct   = Math.min((teamTotalGP / TARGET_GP) * 100, 100);
  const remainingGP = Math.max(0, TARGET_GP - teamTotalGP);
  const targetReached = teamTotalGP >= TARGET_GP;

  return (
    <div className="page-enter page-wrap" style={{ maxWidth: '1200px' }}>

      {/* Header */}
      <div className="page-header">
        <div>
          <div className="label" style={{ marginBottom: '6px' }}>Team Performance</div>
          <h1 style={{ fontSize: '1.6rem' }}>Team View</h1>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <select
            value={quarter}
            onChange={e => { setQuarter(e.target.value); setMonth(''); setSelectedRep(null); }}
            className="input"
            style={{ width: 'auto', minWidth: '140px' }}
          >
            {generateQuarterOptions().map(q => (
              <option key={q} value={q}>{q}</option>
            ))}
          </select>
          <select
            value={month}
            onChange={e => { setMonth(e.target.value); setSelectedRep(null); }}
            className="input"
            style={{ width: 'auto', minWidth: '150px', borderColor: month ? 'var(--border-bright)' : undefined }}
            aria-label="Filter by month"
          >
            <option value="">All months</option>
            {months.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </div>
      </div>

      {/* Team cumulative totals (all reps) */}
      <div className="kpi-grid-6" style={{ gap: '12px', marginBottom: '16px' }}>
        <TeamKpiCard label={`Total HW/SW ${periodLabel} GP`}              value={fmt(teamHwSwGP)}       color="var(--blue-primary)" />
        <TeamKpiCard label={`Total Prof Svcs ${periodLabel} GP`}          value={fmt(teamServicesGP)}   color="var(--blue-primary)" />
        <TeamKpiCard label={`Total Managed Svcs (MSA) ${periodLabel} GP`} value={fmt(teamMsaGP)}        color="var(--blue-primary)" />
        <TeamKpiCard label={`Total Renewal MSA ${periodLabel} GP`}        value={fmt(teamRenewalMsaGP)} color="var(--blue-primary)" />
        <TeamKpiCard label={`Total FOD ${periodLabel} GP`}                value={fmt(teamFodGP)}        color="var(--blue-primary)" />
        <TeamKpiCard label={`Team Commission ${periodLabel}`}             value={fmt(teamTotal)}        color="var(--green)" />
      </div>

      {/* Progress to target — total shipped GP across all categories */}
      <div className="card" style={{ padding: '24px', marginBottom: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <div className="label" style={{ marginBottom: '4px' }}>
              Team Progress to Target{month ? ` · full ${quarter} (quota is quarterly)` : ''}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
              <span style={{ fontSize: '1.4rem', fontWeight: 600, fontFamily: 'var(--font-data)', color: 'var(--blue-primary)' }}>
                {fmt(teamTotalGP)}
              </span>
              <span style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', fontFamily: 'var(--font-data)' }}>
                of {fmt(TARGET_GP)} total GP
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="label" style={{ marginBottom: '4px' }}>{targetReached ? 'Target Reached' : 'Remaining'}</div>
            <div style={{
              fontSize: '1.1rem', fontWeight: 600, fontFamily: 'var(--font-data)',
              color: targetReached ? 'var(--green)' : 'var(--amber)',
            }}>
              {targetReached ? `✓ ${targetPct.toFixed(0)}%` : `${fmt(remainingGP)} away`}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{
          height: '10px', background: 'var(--bg-elevated)',
          borderRadius: '5px', overflow: 'hidden', position: 'relative',
        }}>
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: `${targetPct}%`,
            background: targetReached
              ? 'linear-gradient(90deg, var(--green), #4ade80)'
              : 'linear-gradient(90deg, var(--blue-primary), #5b9bd5)',
            borderRadius: '5px',
            transition: 'width 0.6s ease',
            boxShadow: '0 0 8px rgba(0,121,194,0.6)',
          }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-data)' }}>
            {targetPct.toFixed(1)}%
          </span>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-data)' }}>
            {fmt(TARGET_GP)}
          </span>
        </div>
      </div>

      <div className={selectedRep ? 'split-2' : undefined} style={selectedRep ? undefined : { display: 'grid', gridTemplateColumns: '1fr', gap: '24px' }}>

        {/* Leaderboard */}
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
            <h3>Leaderboard — {quarter}{monthLabel && <span style={{ color: 'var(--blue-primary)' }}> · {monthLabel}</span>}</h3>
          </div>
          {loading ? (
            <LeaderboardSkeleton />
          ) : reps.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px' }}>
              No data for {monthLabel ? `${monthLabel} ${quarter.split('-')[0]}` : 'this quarter'}.
            </div>
          ) : (
            <div>
              {reps.map((rep, idx) => (
                <div
                  key={rep.rep_id}
                  onClick={() => setSelectedRep(selectedRep?.rep_id === rep.rep_id ? null : rep)}
                  style={{
                    padding: '16px 24px',
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                    background: selectedRep?.rep_id === rep.rep_id ? 'var(--blue-subtle)' : 'transparent',
                    transition: 'background 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                >
                  {/* Rank */}
                  <div style={{
                    width: '28px', height: '28px', flexShrink: 0,
                    borderRadius: '50%',
                    background: idx === 0 ? 'rgba(245,158,11,0.15)' : 'var(--bg-elevated)',
                    border: `1px solid ${idx === 0 ? 'var(--amber)' : 'var(--border)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: 700,
                    color: idx === 0 ? 'var(--amber)' : 'var(--text-muted)',
                    fontFamily: 'var(--font-data)',
                  }}>
                    {idx + 1}
                  </div>

                  {/* Name + bar */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '6px' }}>
                      {rep.rep_name}
                    </div>
                    <div style={{
                      height: '4px', background: 'var(--bg-elevated)', borderRadius: '2px', overflow: 'hidden',
                    }}>
                      <div style={{
                        height: '100%', borderRadius: '2px',
                        width: `${teamTotal > 0 ? (rep.total / teamTotal) * 100 : 0}%`,
                        background: 'linear-gradient(90deg, var(--blue-primary), #5b9bd5)',
                        transition: 'width 0.6s ease',
                      }} />
                    </div>
                  </div>

                  {/* Amount */}
                  <div style={{
                    fontSize: '15px', fontWeight: 600,
                    fontFamily: 'var(--font-data)',
                    color: 'var(--green)',
                    flexShrink: 0,
                  }}>
                    {fmt(rep.total)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Rep drill-down */}
        {selectedRep && (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <h3>{selectedRep.rep_name}{monthLabel && <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.85em' }}> · {monthLabel}</span>}</h3>
              <button onClick={() => setSelectedRep(null)} className="btn btn-ghost btn-sm">✕ Close</button>
            </div>

            {/* Rep KPIs */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px',
              background: 'var(--border)', borderBottom: '1px solid var(--border)',
            }}>
              {[
                { label: 'Total Commission', value: fmt(selectedRep.total), color: 'var(--green)' },
                { label: 'HW/SW Commission', value: fmt(selectedRep.hwSw), color: 'var(--blue-primary)' },
                { label: 'Services Commission', value: fmt(selectedRep.services), color: 'var(--text-accent)' },
                { label: 'HW/SW Shipped GP', value: fmt(selectedRep.hwSwGP), color: 'var(--text-primary)' },
              ].map(kpi => (
                <div key={kpi.label} style={{ padding: '16px 20px', background: 'var(--bg-card)' }}>
                  <div className="label" style={{ marginBottom: '6px' }}>{kpi.label}</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 600, fontFamily: 'var(--font-data)', color: kpi.color }}>
                    {kpi.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Monthly breakdown */}
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th style={{ textAlign: 'right' }}>HW/SW GP</th>
                    <th style={{ textAlign: 'right' }}>QTD Cumul</th>
                    <th style={{ textAlign: 'right' }}>Commission</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(allMonthsByRep[selectedRep.rep_id] || []).slice().sort((a,b) => a.payout_month.localeCompare(b.payout_month)).map(m => (
                    <tr key={m.payout_month} style={month && m.payout_month === month
                      ? { background: 'var(--blue-subtle)', boxShadow: 'inset 3px 0 0 var(--blue-primary)' }
                      : month ? { opacity: 0.55 } : undefined}>
                      <td style={{ fontWeight: 500 }}>{m.payout_month}</td>
                      <td className="td-number">{fmt(m.hw_sw_gp_this_month)}</td>
                      <td className="td-number" style={{ color: 'var(--text-accent)' }}>
                        {fmt(m.hw_sw_cumulative_qtd)}
                      </td>
                      <td className="td-number" style={{ color: 'var(--green)', fontWeight: 600 }}>
                        {fmt(m.total_payout)}
                      </td>
                      <td>
                        <span className={`badge badge-${m.status}`}>{m.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TeamKpiCard({ label, value, color }) {
  return (
    <div className="card" style={{ padding: '18px 16px' }}>
      <div className="label" style={{ marginBottom: '10px', minHeight: '26px' }}>{label}</div>
      <div style={{
        fontSize: '1.25rem', fontWeight: 600,
        fontFamily: 'var(--font-data)', color,
        letterSpacing: '-0.02em',
        whiteSpace: 'nowrap',
      }}>
        {value}
      </div>
    </div>
  );
}

function generateQuarterOptions() {
  const opts = [];
  const now = new Date();
  for (let y = now.getFullYear(); y >= now.getFullYear() - 1; y--) {
    for (let q = 4; q >= 1; q--) {
      opts.push(`${y}-Q${q}`);
    }
  }
  return opts;
}

function LeaderboardSkeleton() {
  return (
    <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {[1,2,3,4,5].map(i => (
        <div key={i} className="skeleton" style={{ height: '52px', borderRadius: '8px' }} />
      ))}
    </div>
  );
}